import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { SimpleModal } from "../../components/SimpleModal";
import { Plus, Trash2, Users, AlertCircle, Edit, Lock, Unlock } from "lucide-react";

export default function CompanyVendors() {
  const [vendors, setVendors] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/client/vendors");
      setVendors(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setError(null);
    try {
      const payload = {
        first_name: editing.first_name,
        last_name: editing.last_name,
        email: editing.email,
        phone: editing.phone || null,
      };
      if (editing.pin) payload.pin = editing.pin;
      if (editing.id) {
        await api.put(`/client/vendors/${editing.id}`, payload);
      } else {
        await api.post("/client/vendors", payload);
      }
      setEditing(null);
      load();
    } catch (e) { setError(formatApiError(e)); }
  };

  const toggleActive = async (v) => {
    setError(null);
    try {
      await api.put(`/client/vendors/${v.id}`, { active: !v.active });
      load();
    } catch (e) { setError(formatApiError(e)); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Supprimer le vendeur ${v.first_name} ${v.last_name} ?`)) return;
    try {
      await api.delete(`/client/vendors/${v.id}`);
      load();
    } catch (e) { setError(formatApiError(e)); }
  };

  return (
    <div data-testid="company-vendors-page">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <div className="lc-small-label mb-1">Espace administrateur</div>
          <h1 className="display text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-3">
            <Users className="w-7 h-7 text-[var(--lc-orange)]" /> Vendeurs
          </h1>
        </div>
        <button
          onClick={() => setEditing({ first_name: "", last_name: "", email: "", phone: "", pin: "" })}
          data-testid="company-add-vendor"
          className="lc-btn-primary inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Nouveau vendeur
        </button>
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      )}

      {loading && <div className="text-sm text-[var(--lc-gray)]">Chargement…</div>}
      {!loading && vendors.length === 0 && (
        <div className="lc-card p-10 text-center" data-testid="no-vendors">
          <div className="text-base font-semibold mb-1">Aucun vendeur</div>
          <p className="text-sm text-[var(--lc-gray)]">Créez les profils de vos vendeurs — chacun aura son code PIN à 4 chiffres.</p>
        </div>
      )}

      <div className="grid gap-3" data-testid="vendors-list">
        {vendors.map((v) => (
          <div key={v.id} data-testid={`vendor-row-${v.id}`} className={`lc-card p-4 sm:p-5 flex items-start justify-between gap-3 flex-wrap ${!v.active ? "opacity-60" : ""}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[var(--lc-orange-light)] text-[var(--lc-orange)] flex items-center justify-center text-sm font-black shrink-0">
                {(v.first_name[0] || "") + (v.last_name[0] || "")}
              </div>
              <div>
                <div className="font-semibold flex items-center gap-2">
                  {v.first_name} {v.last_name}
                  {!v.active && <span className="text-[10px] uppercase tracking-wider font-bold text-red-600 bg-red-50 border border-red-200 rounded px-1.5 py-0.5">Restreint</span>}
                </div>
                <div className="text-xs text-[var(--lc-gray)]">{v.email}{v.phone ? ` · ${v.phone}` : ""}</div>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => toggleActive(v)}
                data-testid={`vendor-toggle-${v.id}`}
                className="px-3 py-1.5 text-xs font-semibold border border-[var(--lc-border)] rounded hover:border-[var(--lc-black)] inline-flex items-center gap-1.5"
                title={v.active ? "Restreindre l'accès" : "Réactiver l'accès"}
              >
                {v.active ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
                {v.active ? "Restreindre" : "Réactiver"}
              </button>
              <button
                onClick={() => setEditing({ ...v, pin: "" })}
                data-testid={`vendor-edit-${v.id}`}
                className="px-3 py-1.5 text-xs font-semibold border border-[var(--lc-border)] rounded hover:border-[var(--lc-black)] inline-flex items-center gap-1.5"
              >
                <Edit className="w-3 h-3" /> Modifier
              </button>
              <button
                onClick={() => remove(v)}
                data-testid={`vendor-delete-${v.id}`}
                className="px-3 py-1.5 text-xs font-semibold border border-red-300 text-red-600 rounded"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <SimpleModal title={editing.id ? "Modifier le vendeur" : "Nouveau vendeur"} onClose={() => setEditing(null)} onSave={save}>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="lc-label">Prénom</label><input data-testid="vendor-first-name" className="lc-input" value={editing.first_name} onChange={(e) => setEditing({ ...editing, first_name: e.target.value })} /></div>
            <div><label className="lc-label">Nom</label><input data-testid="vendor-last-name" className="lc-input" value={editing.last_name} onChange={(e) => setEditing({ ...editing, last_name: e.target.value })} /></div>
            <div><label className="lc-label">Email</label><input data-testid="vendor-email" type="email" className="lc-input" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} />
              <p className="text-[11px] text-[var(--lc-gray)] mt-1">Utilisé pour les codes de validation, confirmations et BL.</p>
            </div>
            <div><label className="lc-label">Téléphone</label><input data-testid="vendor-phone" className="lc-input" value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} /></div>
            <div>
              <label className="lc-label">Code PIN (4 chiffres){editing.id ? " — laisser vide pour ne pas changer" : " — facultatif"}</label>
              <input
                data-testid="vendor-pin"
                type="text"
                inputMode="numeric"
                maxLength={4}
                className="lc-input font-mono tracking-[0.4em]"
                value={editing.pin || ""}
                onChange={(e) => setEditing({ ...editing, pin: e.target.value.replace(/\D/g, "") })}
                placeholder="0000"
              />
              {!editing.id && (
                <p className="text-[11px] text-[var(--lc-gray)] mt-1">Si vous laissez vide, le vendeur choisira lui-même son code PIN lors de sa première connexion.</p>
              )}
            </div>
          </div>
        </SimpleModal>
      )}
    </div>
  );
}
